import { supabase, enhancedQuery } from '@/integrations/supabase/client';

export interface Announcement {
  id: string;
  organisation_id: string;
  comments: string;
  created_at: string;
  end_time: string;
  is_active: boolean;
  created_by: string;
  updated_at: string;
  organization?: {
    name: string;
    code: string;
  };
}

export interface AnnouncementCreateInput {
  organisation_id: string;
  comments: string;
  created_by: string;
}

// Get active announcements (public display)
export const getActiveAnnouncements = async (): Promise<Announcement[]> => {
  const { data, error } = await supabase
    .from('announcements')
    .select(`
      *,
      organization:organizations(name, code)
    `)
    .eq('is_active', true)
    .gt('end_time', new Date().toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching active announcements:', error);
    throw new Error(`Failed to fetch active announcements: ${error.message}`);
  }

  return data || [];
};

// Get all announcements for an organization
export const getOrganizationAnnouncements = async (organizationId: string): Promise<Announcement[]> => {
  return enhancedQuery(async () => {
    return await supabase
      .from('announcements')
      .select(`
        *,
        organization:organizations(name, code)
      `)
      .eq('organisation_id', organizationId)
      .order('created_at', { ascending: false });
  });
};

// Get all announcements (admin view)
export const getAllAnnouncements = async (): Promise<Announcement[]> => {
  return enhancedQuery(async () => {
    return await supabase
      .from('announcements')
      .select(`
        *,
        organization:organizations(name, code)
      `)
      .order('created_at', { ascending: false });
  });
};

// Create new announcement
export const createAnnouncement = async (input: AnnouncementCreateInput): Promise<Announcement> => {
  // Check active count before creating
  const { data: countData, error: countError } = await supabase
    .rpc('get_active_announcements_count', { org_id: input.organisation_id });

  if (countError) {
    throw new Error(`Failed to check active announcements count: ${countError.message}`);
  }

  if (countData >= 5) {
    throw new Error('Maximum 5 active announcements allowed');
  }

  return enhancedQuery(async () => {
    return await supabase
      .from('announcements')
      .insert(input as any)
      .select(`
        *,
        organization:organizations(name, code)
      `)
      .single();
  });
};

// Stop announcement (set is_active to false)
export const stopAnnouncement = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from('announcements')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', id);

  if (error) {
    throw new Error(`Failed to stop announcement: ${error.message}`);
  }
};

// Get active announcements count for an organization
export const getActiveAnnouncementsCount = async (organizationId: string): Promise<number> => {
  const { data, error } = await supabase
    .rpc('get_active_announcements_count', { org_id: organizationId });

  if (error) {
    throw new Error(`Failed to get active announcements count: ${error.message}`);
  }

  return data || 0;
};